import DeleteIcon from '@mui/icons-material/Delete';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Tooltip, Typography } from '@mui/material';
import { AlertDialog } from '../../../components/AlertDialog';

type DeleteAnnouncementDialogProps = {
  title: string;
  /** Rejects when the delete failed; the dialog then stays open with an error. */
  onConfirm: () => Promise<void>;
  showText?: boolean;
};

export const DeleteAnnouncementDialog = ({
  title,
  onConfirm,
  showText = false,
}: DeleteAnnouncementDialogProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const label = t('announcements.delete', { title });

  const handleClose = () => {
    setOpen(false);
    setError(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    setError(false);
    try {
      await onConfirm();
      setOpen(false);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {showText ? (
        <Button
          variant="warningSecondary"
          startIcon={<DeleteIcon aria-hidden="true" />}
          onClick={() => setOpen(true)}
          size="small"
          aria-label={label}
        >
          {t('announcements.deleteDialog.confirm')}
        </Button>
      ) : (
        <Tooltip title={label}>
          <Button
            variant="warningLink"
            aria-label={label}
            onClick={() => setOpen(true)}
            size="small"
            sx={{
              minWidth: 'unset',
              width: '32px',
              height: '32px',
              borderRadius: '50%',
            }}
          >
            <DeleteIcon aria-hidden="true" />
          </Button>
        </Tooltip>
      )}
      <AlertDialog
        variant="alert"
        open={open}
        onClose={handleClose}
        title={t('announcements.deleteDialog.title')}
        description={t('announcements.deleteDialog.description', { title })}
        actions={{
          cancel: {
            label: t('announcements.deleteDialog.cancel'),
            onClick: handleClose,
          },
          confirm: {
            label: t('announcements.deleteDialog.confirm'),
            onClick: handleDelete,
            loading,
            loadingLabel: t('announcements.deleteDialog.deleting'),
          },
        }}
      >
        {error && (
          <Typography
            role="alert"
            sx={{ mt: 12, color: 'var(--color-dark-grey-red)', fontSize: 13 }}
          >
            {t('announcements.deleteDialog.error')}
          </Typography>
        )}
      </AlertDialog>
    </>
  );
};
